"use client";

import { useActionState, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useFormStatus } from "react-dom";
import { useRouter } from "@frontend/lib/navigation";
import { counterOfferAction, type ActionState } from "@frontend/lib/actions";
import { PaymentPlanPicker } from "@frontend/components/payment-plan-picker";
import type { CollabRoom } from "@creators/backend/lib/collabs/queries";
import {
  presetKeyForSchedule,
  scheduleFingerprint,
  type SchedulePresetKey,
} from "@creators/backend/lib/collabs/schedule";
import { formatINR, paiseToRupeeString, parseRupeesToPaise } from "@creators/backend/lib/money";

const DURATION_OPTIONS = [
  { id: "keep", label: "Keep date" },
  { id: "7", label: "+7 days" },
  { id: "14", label: "+14 days" },
  { id: "custom", label: "Custom" },
] as const;

type DurationId = (typeof DURATION_OPTIONS)[number]["id"];

function todayString(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDaysToDate(base: string, days: number): string {
  const d = new Date(`${base}T12:00:00`);
  if (Number.isNaN(d.getTime())) {
    const t = new Date();
    t.setDate(t.getDate() + days);
    return t.toISOString().slice(0, 10);
  }
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function CounterOfferDialog({ room }: { room: CollabRoom }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-ink transition hover:bg-canvas"
      >
        Counter offer
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-4 sm:items-center"
          onClick={() => setOpen(false)}
        >
          <div
            className="max-h-[min(640px,90dvh)] w-full max-w-md overflow-y-auto rounded-2xl bg-surface shadow-xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="counter-offer-title"
          >
            <CounterOfferForm room={room} onClose={() => setOpen(false)} />
          </div>
        </div>
      ) : null}
    </>
  );
}

function CounterOfferForm({
  room,
  onClose,
  embedded = false,
}: {
  room: CollabRoom;
  onClose: () => void;
  embedded?: boolean;
}) {
  const router = useRouter();
  const offer = room.currentOffer!;
  const presetKey = presetKeyForSchedule(offer.schedule);

  const [state, formAction] = useActionState<ActionState, FormData>(counterOfferAction, {});
  const [amount, setAmount] = useState(
    offer.amountTotal > 0 ? paiseToRupeeString(offer.amountTotal) : "",
  );
  const [plan, setPlan] = useState<SchedulePresetKey>(presetKey);

  const scheduleKey = scheduleFingerprint(offer.schedule);

  // Keep selection in sync if the room refreshes with a different current plan.
  useEffect(() => {
    setPlan(presetKeyForSchedule(offer.schedule));
    setAmount(offer.amountTotal > 0 ? paiseToRupeeString(offer.amountTotal) : "");
    setCustomDate(offer.deliverBy);
  }, [offer.id, offer.amountTotal, offer.deliverBy, scheduleKey, offer.schedule]);
  const [duration, setDuration] = useState<DurationId>("keep");
  const [customDate, setCustomDate] = useState(offer.deliverBy);
  const handled = useRef<ActionState | null>(null);

  const amountPaise = useMemo(() => {
    const parsed = parseRupeesToPaise(amount);
    return parsed && parsed > 0 ? parsed : null;
  }, [amount]);

  const deliverBy =
    duration === "keep"
      ? offer.deliverBy
      : duration === "custom"
        ? customDate
        : addDaysToDate(offer.deliverBy || todayString(), Number(duration));

  useEffect(() => {
    if (state === handled.current) return;
    handled.current = state;
    if (state.ok) {
      onClose();
      router.refresh();
    }
  }, [state, onClose, router]);

  return (
    <form action={formAction} className={embedded ? "pb-2" : "p-5"}>
      {!embedded ? (
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 id="counter-offer-title" className="font-semibold text-ink">
              Counter offer
            </h3>
            <p className="mt-0.5 text-sm text-ink-muted">
              Change amount, payment plan, or delivery time.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-sm text-ink-muted hover:bg-canvas hover:text-ink"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      ) : (
        <p className="mb-3 text-xs text-ink-muted">
          Change amount, payment plan, or delivery time. Brief stays the same.
        </p>
      )}

      <input type="hidden" name="collabId" value={room.collab.id} />
      <input type="hidden" name="deliverBy" value={deliverBy} />
      <input type="hidden" name="brief" value={offer.brief} />
      <input type="hidden" name="revisionsIncluded" value={offer.revisionsIncluded} />
      <input type="hidden" name="livePeriodDays" value={offer.livePeriodDays} />

      <div className="space-y-4">
        <CompactField label="Amount" hint={`Current ${formatINR(offer.amountTotal)}`}>
          <div className="relative">
            <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-muted">
              ₹
            </span>
            <input
              name="amount"
              inputMode="numeric"
              required
              autoFocus={!embedded}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              className="field-compact tabular pl-7"
            />
          </div>
        </CompactField>

        <PaymentPlanPicker
          name="preset"
          value={plan}
          onChange={setPlan}
          amountPaise={amountPaise}
        />

        <CompactField
          label="Delivery / time"
          hint={
            duration === "keep"
              ? `Stays ${offer.deliverBy}`
              : duration === "custom"
                ? "Pick YYYY-MM-DD"
                : `New date ${deliverBy}`
          }
        >
          <div className="grid grid-cols-4 gap-1.5">
            {DURATION_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setDuration(opt.id)}
                className={`rounded-lg border px-1.5 py-2 text-center text-[11px] font-medium transition sm:text-xs ${
                  duration === opt.id
                    ? "border-ink bg-ink text-canvas"
                    : "border-line bg-surface text-ink hover:bg-canvas"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </CompactField>

        {duration === "custom" ? (
          <CompactField label="Deliver by">
            <input
              type="date"
              value={customDate}
              onChange={(event) => setCustomDate(event.target.value)}
              className="field-compact"
              required
            />
          </CompactField>
        ) : null}
      </div>

      {state.error ? <p className="mt-3 text-sm text-warn-700">{state.error}</p> : null}

      <div className="mt-4 flex gap-2">
        <SendButton />
        {embedded ? null : (
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-xl px-4 py-2.5 text-sm font-medium text-ink-muted hover:bg-canvas"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

function CompactField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-ink-muted">{label}</span>
      {hint ? <span className="mt-0.5 block text-[11px] text-ink-subtle">{hint}</span> : null}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function SendButton() {
  const { pending } = useFormStatus();
  return (
    <button
      type="submit"
      disabled={pending}
      className="inline-flex flex-1 items-center justify-center rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-medium text-canvas transition hover:bg-brand-700 disabled:opacity-60"
    >
      {pending ? "Sending…" : "Send counter"}
    </button>
  );
}
